<script>
  // ══════════════════════════════════════════════════════════════════════════
  // ОБЩЕСТВО: Бизнес · Организация · Достижения — перенос society.tsx 1:1.
  // Источники данных прежние (empire/faction/retn), изменена только подача.
  // ══════════════════════════════════════════════════════════════════════════
  import { t } from '../../i18n.js';
  import { money, num } from '../partsfn.js';
  import { propertyPhoto } from '../photofn.js';
  import Empty from '../empty.svelte';
  import Scroll from '../ui/scroll.svelte';
  import Sechead from '../ui/sechead.svelte';
  import Panel from '../ui/panel.svelte';
  import Metric from '../ui/metric.svelte';
  import Mgrid from '../ui/mgrid.svelte';
  import Prog from '../ui/prog.svelte';
  import Chip from '../ui/chip.svelte';
  import Row from '../ui/row.svelte';
  import Rows from '../ui/rows.svelte';
  import Line from '../ui/line.svelte';
  import Grid from '../ui/grid.svelte';
  import Jc from '../ui/jc.svelte';

  export let part = 'business';
  export let empire = {};
  export let faction = {};
  export let retn = {};

  // ── Бизнес
  $: holdings = empire.holdings ?? [];
  $: total = holdings.reduce((s, h) => s + (h.treasuryBank || 0), 0);

  // ── Организация
  $: inFaction = !!faction.factionName;
  $: roster = faction.roster ?? [];
  $: turf = (faction.turf ?? []).filter((x) => x.mine);

  // ── Достижения (досье аккаунта: настоящие цифры вместо выдуманных ачивок)
  const hhmmLong = (min) => {
    const m = Math.max(0, Math.round(min ?? 0));
    return `${Math.floor(m / 60)} ч ${m % 60} мин`;
  };
  $: r = retn ?? {};

  /** Названия привилегий по уровню — те же, что в магазине. */
  const VIP_NAMES = { 1: 'VIP Silver', 2: 'VIP Gold', 3: 'VIP Platinum', 4: 'VIP Diamond' };
</script>

{#if part === 'business'}
  <Scroll>
    <Sechead kicker={t('m.bz.kicker')} title={t('m.bz.title')}>
      <svelte:fragment slot="meta">
        {#if holdings.length}{t('m.bz.treasury')}: <b>{money(total)}</b>{/if}
      </svelte:fragment>
    </Sechead>
    {#if holdings.length === 0}
      <Panel><Empty icon="business" text={t('m.bz.none')} /></Panel>
    {:else}
      <Mgrid style="margin-bottom:14px">
        <Metric gold label={t('m.bz.treasury')} value={money(total)} />
        <Metric label={t('m.total')} value={num(holdings.length)} />
      </Mgrid>
      <Grid size="wide">
        {#each holdings as h (h.bizId)}
          <Panel>
            <!-- фото по типу предприятия — папки majestic совпадают с нашими kind -->
            <div class="mn-shot tall photo" style="margin-bottom:10px">
              <img src={propertyPhoto(h.kind || 'item_shops', 2)} alt="" on:error={(e) => { e.currentTarget.style.display = 'none'; }} />
            </div>
            <Sechead kicker={h.myRole === 'owner' ? t('m.bz.owner') : t('m.bz.partner')} title={h.name} divider={false} />
            <Rows>
              <Line k={t('m.bz.treasury')} v={money(h.treasuryBank)} tone="gold" />
              <Line k={t('m.bz.share')} v={`${h.myPct}%`} />
              <Line k={t('m.bz.manager')} v={h.managerLabel ?? t('m.bz.noManager')} tone={h.managerLabel ? undefined : 'off'} />
            </Rows>
          </Panel>
        {/each}
      </Grid>
    {/if}
  </Scroll>

{:else if part === 'organization'}
  <Scroll>
    <Sechead kicker={t('m.or.kicker')} title={inFaction ? faction.factionName : t('m.or.title')}>
      <svelte:fragment slot="meta">
        {#if inFaction}{t('m.or.rank')}: <b>{faction.membership?.rank ?? 0}</b>{/if}
      </svelte:fragment>
    </Sechead>
    {#if !inFaction}
      <Panel><Empty icon="organization" text={t('m.or.none')} /></Panel>
    {:else}
      <Row wrap style="align-items:stretch">
        <Panel class="ui-grow" style="min-width:280px">
          <Sechead title={t('m.or.members')} divider={false}>
            <svelte:fragment slot="meta"><b>{roster.length}</b></svelte:fragment>
          </Sechead>
          <Rows>
            {#each roster as m (m.cid)}
              <Line k={`#${m.cid}`}><Chip tone={m.rank > 0 ? 'gold' : 'default'}>{m.rankName}</Chip></Line>
            {/each}
          </Rows>
        </Panel>
        <Panel class="ui-grow" style="min-width:280px">
          <Sechead title={t('m.or.turf')} divider={false}>
            <svelte:fragment slot="meta"><b>{turf.length}</b></svelte:fragment>
          </Sechead>
          {#if turf.length === 0}
            <div class="hint">{t('m.or.noTurf')}</div>
          {:else}
            {#each turf as nd (nd.id)}
              <div class="ui-field">
                <div class="labrow">
                  <span class="lab" style="margin-bottom:0">{nd.zone}</span>
                  <span class="note mn-num">{nd.control}% {t('m.or.control')}</span>
                </div>
                <Prog value={nd.control} />
              </div>
            {/each}
          {/if}
        </Panel>
      </Row>
    {/if}
  </Scroll>

{:else}
  <Scroll>
    <Sechead kicker={t('m.ac.kicker')} title={t('m.ac.title')}>
      <svelte:fragment slot="meta">
        {#if r.login}{t('m.ac.account')}: <b>{r.login}</b>{/if}
      </svelte:fragment>
    </Sechead>

    <Mgrid>
      <Metric gold label={t('m.ac.level')} value={num(r.level ?? 0)} />
      <Metric label={t('m.ac.exp')} value={num(r.exp ?? 0)} />
      <Metric label={t('m.ac.daysOn')} value={`${num(r.daysOnServer ?? 0)} ${t('m.ac.days')}`} smallValue />
      <Metric label={t('m.ac.since')} value={r.createDate || '—'} smallValue />
    </Mgrid>

    <Panel style="margin-top:14px">
      <Sechead title={t('m.ac.time')} divider={false} />
      <Rows>
        <Line k={t('m.ac.today')} v={hhmmLong(r.todayTime)} />
        <Line k={t('m.ac.month')} v={hhmmLong(r.monthTime)} />
        <Line k={t('m.ac.total')} v={hhmmLong(r.totalTime)} tone="gold" />
      </Rows>
    </Panel>

    <Panel style="margin-top:14px">
      <Sechead title={t('m.ac.wealth')} divider={false} />
      <Rows>
        <Line k={t('m.ov.cash')} v={`$${num(r.money ?? 0)}`} />
        <Line k={t('m.ov.bank')} v={`$${num(r.bank ?? 0)}`} />
        <Line k={t('m.ac.jc')} tone="gold"><Jc v={r.redbucks ?? 0} /></Line>
        <Line k={t('m.ac.cars')} v={num(r.vehicles ?? 0)} />
        <Line k={t('m.ac.house')} v={r.house ? `#${r.houseId}` : t('m.ac.no')} />
        <Line k={t('m.ac.biz')} v={r.biz ? num(r.biz) : t('m.ac.no')} />
      </Rows>
    </Panel>

    <Panel style="margin-top:14px">
      <Sechead title={t('m.ac.status')} divider={false} />
      <Rows>
        <Line k={t('m.ac.vip')}>
          {#if r.vipLvl}<Chip tone="gold">{VIP_NAMES[r.vipLvl] ?? r.vipLvl} · {t('m.ac.until')} {r.vipUntil}</Chip>{:else}{t('m.ac.no')}{/if}
        </Line>
        <Line k={t('m.ac.sub')}>
          {#if r.subscribe}<Chip tone="pos">{t('m.ac.active')}</Chip>{:else}{t('m.ac.no')}{/if}
        </Line>
        <Line k={t('m.ac.warns')} v={num(r.warns ?? 0)} tone={r.warns ? 'neg' : undefined} />
        <Line k={t('m.ac.wanted')} v={num(r.wanted ?? 0)} />
        {#if r.fraction}<Line k={t('m.ov.faction')} v={r.fraction} />{/if}
        {#if r.organization}<Line k={t('m.ac.org')} v={r.organization} />{/if}
        {#if r.wedding}<Line k={t('m.ac.wedding')} v={r.wedding} />{/if}
      </Rows>
    </Panel>
  </Scroll>
{/if}
